import type { TFunction } from 'i18next'

import type { ProductGroupDetails, ProductGroupGalleryImage } from 'models/purchaseFlowRevamp'
import generated from './productGroupDetails.generated.json'

/**
 * Bundled product-group detail content. The images and term icons are scraped from the public
 * portfolio pages on bkbn.com by `scripts/portfolioDetails/portfolioDetails.mjs`; every text line
 * (the terms and the deliverables) lives in the translation files under
 * `purchase_flow_revamp:product_group_details.<group>`, so it is edited and translated in Weblate.
 *
 * INTERIM SOURCE. Read it through `useProductGroupDetails`, never directly: the
 * hook is the single place that changes when the backend starts serving this.
 */

interface GeneratedGroup {
  termIcons: string[]
  gallery: Record<string, ProductGroupGalleryImage[]>
}

interface GeneratedFile {
  generatedAt: string
  source: string
  groups: Record<string, GeneratedGroup>
}

const DATA = generated as GeneratedFile

/** The site publishes its pictures for en/de/es/nl only, so fr and it show the English ones. */
const FALLBACK_LOCALE = 'en'

/** The numbered lines under one translation key, in order; none when the key is absent. */
const translatedLines = (t: TFunction, key: string, lng: string): string[] => {
  const value = t(key, { returnObjects: true, lng }) as unknown
  if (!value || typeof value !== 'object') return []
  // Integer-like keys enumerate in numeric order, so "10" follows "9".
  return Object.values(value as Record<string, string>)
}

/**
 * Detail content for a group in `locale`, or undefined when the group has no entry — a group the
 * scraper's map does not cover, which is the state every new product group starts in.
 */
export const bundledProductGroupDetails = (
  t: TFunction,
  productGroupCode?: string,
  locale?: string
): ProductGroupDetails | undefined => {
  if (!productGroupCode) return undefined
  const group = DATA.groups[productGroupCode]
  if (!group) return undefined

  const language = (locale ?? FALLBACK_LOCALE).split('-')[0].toLowerCase()
  const key = `purchase_flow_revamp:product_group_details.${productGroupCode}`
  const termTexts = translatedLines(t, `${key}.terms`, language)

  return {
    terms: group.termIcons
      .map((icon, index) => ({ icon, text: termTexts[index] }))
      .filter((term): term is { icon: string, text: string } => Boolean(term.text)),
    gallery: group.gallery[language] ?? group.gallery[FALLBACK_LOCALE] ?? [],
    deliverables: translatedLines(t, `${key}.deliverables`, language),
  }
}

/** When the bundled content was scraped. Surfaced for support, not for display. */
export const productGroupDetailsGeneratedAt = DATA.generatedAt
